import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WeatherWidget } from '../WeatherWidget/WeatherWidget';
import { Globe } from 'lucide-react';

export interface PageContainerProps {
  heroImage: string;
  heroHeight?: 'large' | 'small';
  heroTitle?: React.ReactNode;
  heroSubtitle?: React.ReactNode;
  overlayOpacity?: number;
  showWeather?: boolean;
  searchBar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
];

export const PageContainer: React.FC<PageContainerProps> = ({
  heroImage,
  heroHeight = 'small',
  heroTitle,
  heroSubtitle,
  overlayOpacity = 0.45,
  showWeather = false,
  searchBar,
  children,
  className = '',
}) => {
  const { i18n } = useTranslation();
  const heroHeightClass = heroHeight === 'large' ? 'h-[480px]' : 'h-[280px]';
  const [selectedLang, setSelectedLang] = useState(i18n.language || 'en');
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const handleLangSelect = (code: string) => {
    void i18n.changeLanguage(code);
    setSelectedLang(code);
    setShowLangDropdown(false);
  };

  return (
    <div className={`flex-1 overflow-y-auto bg-[#FAFAFA] ${className}`}>
      {/* Hero Section */}
      <div className={`relative ${heroHeightClass} overflow-hidden`}>
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Hero"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Dark Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, rgba(26, 26, 26, ${overlayOpacity}), rgba(26, 26, 26, ${overlayOpacity * 0.5}))`,
          }}
        />

        {/* Search Bar */}
        {searchBar && (
          <div className="absolute top-8 left-8 z-30">
            {searchBar}
          </div>
        )}

        {/* Language Selector - Top Right */}
        <div className="absolute top-8 right-8 z-30">
          <div className="relative">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="flex items-center justify-center w-11 h-11 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-all duration-300 border border-white/30"
            >
              <Globe className="w-5 h-5" />
            </button>
            
            {showLangDropdown && (
              <div className="absolute top-full right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl py-2 min-w-[160px] z-50 border border-white/20">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangSelect(lang.code)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#FDF6E9] transition-colors ${
                      selectedLang === lang.code ? 'text-[#D4A054] font-semibold bg-[#FDF6E9]' : 'text-gray-700'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex flex-col justify-end p-8 pb-12">
          <div className="max-w-3xl">
            {heroTitle && (
              <h1 className="font-display text-white text-shadow text-hero leading-tight mb-2">
                {heroTitle}
              </h1>
            )}
            {heroSubtitle && (
              <p className="text-white/80 text-lg leading-relaxed max-w-2xl">
                {heroSubtitle}
              </p>
            )}
          </div>

          {/* Weather Widget - positioned bottom-right for Home page */}
          {showWeather && (
            <div className="absolute bottom-8 right-8">
              <WeatherWidget />
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-8">
        {children}
      </div>
    </div>
  );
};

export default PageContainer;
